import { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    SettingDTO,
    useSearchSettingApi,
    useSaveSettingApi,
    useDeleteSettingApi,
} from '../../../api/setting-api';
import SvgAdd from '../../../assets/icons/Add';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikCellToggle } from '../../../components/formik';
import { DateCell, ToggleCell } from '../../../components/table/table-cells';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { SaveSettingModal } from './save-setting-modal';
import { LoadingButton } from '../../../components/loading-button';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { CreatedRowInfo } from '../../../components/table/created-row-info';

export function SettingApp() {
    const [showModal, setShowModal] = useState(false);
    const [selectedSetting, setSelectedSetting] = useState<SettingDTO | undefined>();
    const settingInit = useSearchSettingApi();
    const saveSetting = useSaveSettingApi();
    const deleteSetting = useDeleteSettingApi();

    const { result, error, busy, initialized, initializing } = settingInit;

    const columns: IBasicTableColumn<SettingDTO>[] = useMemo(
        () => [
            {
                Header: <T>id</T>,
                name: 'id',
                width: 80,
            },
            {
                Header: <T>value</T>,
                name: 'value',
                width: 80,
            },

            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 100,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedBy',
                width: 100,
                Cell: ModifiedRowInfo,
            },
        ],
        []
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        settingInit.post(null, {});
    }, [initialized, initializing, error, settingInit]);

    const handleDelete = (setting: SettingDTO) => {
        bd.msgbox(
            translate('delete-setting'),
            translate('confirm-delete-setting'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteSetting.remove(
                            setting,
                            () => {
                                settingInit.updateResult({
                                    ...result,
                                    settings: result.settings.filter(b => b.id !== setting.id),
                                });
                                notify.dark(translate('selected-setting-deleted-successfully'));
                                hide();
                                setShowModal(false);
                                setSelectedSetting(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    };

    const handleSave = (setting: SettingDTO) => {
        saveSetting.post(
            null,
            setting,
            res => {
                if (selectedSetting) {
                    settingInit.updateResult({
                        ...result,
                        settings: result.settings.map(b => (b.id === setting.id ? res.setting : b)),
                    });
                } else {
                    settingInit.updateResult({
                        ...result,
                        settings: [res.setting, ...result.settings],
                    });
                }

                setShowModal(false);
                setSelectedSetting(undefined);
                notify.success(
                    selectedSetting
                        ? translate('selected-setting-updated-successfully')
                        : translate('new-setting-created-successfully')
                );
            },
            notify.error
        );
    };

    if (error) return <InitFailed ex={error} onRetry={() => settingInit.post(null, {})} />;
    if (!initialized || initializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="setting"
                    controls={
                        <LoadingButton
                            variant="contained"
                            size="sm"
                            label={<T>new-setting</T>}
                            icon={<SvgAdd />}
                            onClick={() => {
                                setShowModal(true);
                                setSelectedSetting(undefined);
                            }}
                            loading={busy}
                        />
                    }
                />
                <bd.Paper className="h-100">
                    <bd.BasicTable
                        columns={columns}
                        data={result.settings}
                        onRowClick={({ row }) => {
                            setSelectedSetting(row);
                            setShowModal(true);
                        }}
                        hover
                    />

                    <SaveSettingModal
                        show={showModal}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedSetting(undefined);
                        }}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedSetting={selectedSetting}
                        allowEdit={result.allowEdit}
                    />
                </bd.Paper>
            </div>
        </TProvider>
    );
}
